/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { FileLoader } from 'three';
import {
  CapDataDesc,
  DefaultCaps,
  Director,
  Stage,
  Text2DFontLoader,
  DefaultNavigators,
  StageDesc,
  CapNames,
  Events,
  ObjectTouchedEvent,
  ObjectClickedEvent,
} from '@sick-davinci/glow';

import {
  CrownImageType,
  CrownImageCap,
  CrownImageDesc,
  CrownShapeCap,
  CrownShapeDecorationDesc,
  CrownTextCap,
  CrownTextDesc,
  CrownTextDecorationDesc,
  CrownViewCapData,
  ShapeRectangleDesc,
  CrownViewPresentAddDesc,
} from '@sick-davinci/glow-crown';
import {
  CrownConnectionWebSocket,
  CrownProtocolMsgPack,
  LogLevel,
  MsgPackDecoder,
  PointerActionType,
  PointerType,
  CrownDataDesc,
} from '@sick-davinci/device-communication';

import { addStatsWidget } from '@sick-davinci/glow-utils';

import { TextField } from '@sick-davinci/basic-elements';
import { ObjectAddedEvent } from '@sick-davinci/glow/src';

export const dependencies = [TextField];  // avoid wepack removal

/**
 * Demo:    CrownImageViewer
 * Description:
 *  This demo application displays a CrownImage.
 *  Incoming (using Crown API / CrownView - Iface) device data (images, text and shapes) is put on the main view-port.
 *  A 2nd smaller view-port displays a magnified image following the mouse cursor.
 *  Features:
 *  - 2D Navigation
 *  - Capability handling
 *  - ViewCrown / WebSocket connection
 *  - Object interaction
 *  - Multiple view ports
 *  - Camera manipulation
 *  - Special z-buffer handling
 */
export const run = (async (viewParent: HTMLElement) => {

  // ** setup stage *
  const setup: StageDesc = {
    // * 2 different cameras:
    cameras: [
      // main camera for the big picture
      {
        name: 'frontCamera',
        from: [0, 0, 3],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 30,
        near: 0,
        far: 50,
      },
      // assissting camera for the smaller zoomed-in picture: looks at the same destination with a different fov
      {
        name: 'magnifierCamera',
        from: [0, 0, 3],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 1,
      },
    ],
    // one content scene for the image and shapes
    scenes: [
      { name: 'default' },
    ],
    // 2 different viewports:
    viewports: [
      // ** main viewport for the big picture: use whole screen with frontCamera and plane navigator *
      {
        name: 'main',
        scene: 'default',
        camera: 'frontCamera',
        navigator: 'pan',
        bgcolor: 0x007fc3,
      },
      // ** magnifier viewport for the smaller picture: use top right corner of the screen (20% screensize) *
      {
        name: 'mangifier',
        scene: 'default',
        camera: 'magnifierCamera',
        navigator: 'none',
        left: .7,
        top: .1,
        width: .2,
        height: .2,
        bgcolor: 0xf49100,
      },
    ],
  };

  const stage = new Stage(viewParent, setup);

  // * Use pan-only Navigator
  DefaultNavigators.init2D(stage, 'pan');
  stage.activateViewport('main'); // focus on main view port

  // ** handle interaction. IMPORTANT: addEventlistener must be called after stage is created!

  // * set event priority, so the image is always the payload of the OBJECTTOUCHED event
  function onObjectAdded({detail}: CustomEvent<ObjectAddedEvent>) {
    const objName = detail.name;
    const obj = stage.getObjectByName(objName);
    if (obj.children.length > 0 && (obj.children[0] as any).textureType !== undefined) { // this is the image
      obj.children[0].userData.eventPriority = 1;
      viewParent.removeEventListener(Events.OBJECTADDED, onObjectAdded);
    }
  }
  viewParent.addEventListener(Events.OBJECTADDED, onObjectAdded); // remove listener after its purpose is fulfilled

  // * use maxX and relY to to shape scaling: the last image defines the screen width
  let relY = 0;
  let maxX = 1.0;

  // * mouse-move interaction
  const valueElement = document.getElementById('color_picker');
  viewParent.addEventListener(Events.OBJECTTOUCHED, ({detail}: CustomEvent<ObjectTouchedEvent>) => {

    // colorpicker: show color values beneath the mouse cursor
    const n = detail.new as any;
    if (!!n && !!n.object && !!n.object.material && !!n.object.material.uniforms && !!n.object.material.uniforms.texture && !!n.object.material.uniforms.texture.value) {
      const img = n.object.material.uniforms.texture.value.image as unknown as {width: number, height: number, data: Uint8Array};
      const posX = Math.round(img.width * n.uv.x);
      const posY = img.height - Math.round(img.height * (1 - n.uv.y));
      let pos = posX + posY * img.width;
      switch (n.object.textureType) {
        case CrownImageType.UINT8:
          (valueElement as TextField).value = img.data[pos].toString();
          break;
        case CrownImageType.RGB24:
          pos *= 3;
          (valueElement as TextField).value = `${img.data[pos + 2]} / ${img.data[pos + 1]} / ${img.data[pos]}`; // CrownImage = BGR
          break;
        default: break;
      }

      // make the mangifierCamera look at the mouse position
      const cam = stage.getCamera('magnifierCamera');

      const xPos = n.uv.x - 0.5;
      const yPos = -(n.uv.y - 0.5) * relY;
      cam.position.setX(xPos);
      cam.position.setY(yPos);
      cam.lookAt(xPos, yPos, 0);
    }
  });

  // ** add fps-meter to the scene
  addStatsWidget(stage);
  stage.start();  // start visualizing data

  // ** data handling using capabilities *
  const director = new Director(stage);
  DefaultCaps.init(director);
  director.addCapability(new CrownImageCap());
  director.addCapability(new CrownShapeCap());
  director.addCapability(await createTextCap());
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0xffffff });   // everything is in similar brightness

  // * put overlays slightly before the image so they are drawn
  const zPosShapes = 0.00001;
  const zPosText   = 0.0001;  // text has greater priority than shape

  // ** Connect to the device via the ViewCrown using the provided ip and view-id
  const cc = new CrownConnectionWebSocket('127.0.0.1', 'crownMSGPACK', 3000);
  const cp = new CrownProtocolMsgPack();
  const view = new CrownViewCapData(cc, cp, '2D');

  // ** Set all necessary callback functions for the device connection
  view.onPresentStart(() => stage.lockRenderer());
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearTaggedObjects('overlay'));
  view.onPresentRemove((name) => stage.clearObject(stage.getObjectIdByName(name)));
  view.onPresentAddCapData((capData) => {
    const { payload } = capData;
    switch (payload.Type) {
      case CrownTextCap.className:
        // align text on the image
        if (!!payload.Decorations && payload.Decorations.length === 1) {
          (payload.Decorations[0].data as CrownTextDecorationDesc).FlipY = true;  // in image processing Y is up-side-down
        }
        capData.orientation = { scale: [1.0 / maxX, -1.0 / maxX, 1.0 / maxX], position: [-0.5, relY / 2.0, zPosText] };
        capData.tag = 'overlay';  // use tag for clear
        break;
      case CrownShapeCap.className:
        // align shapes on the image
        capData.orientation = { scale: [1.0 / maxX, -1.0 / maxX, 1.0 / maxX], position: [-0.5, relY / 2.0, zPosShapes] };
        capData.tag = 'overlay';  // use tag for clear
        break;
      case CrownImageCap.className:
        if (payload.Iconics.length === 1) {
          // remember image size so shapes and text are aligned on the image
          const img = payload.Iconics[0].data as CrownImageDesc;
          maxX = img.Data.Width;
          relY = img.Data.Height / maxX;
          break;
        }
      default: break;
    }

    director.handleCapData(capData);
  });

  // ** Send some data as soon as connection is established
  view.onConnectionChanged((connected) => {
    if (connected) {
       view.logMessage(LogLevel.INFO, 'Image Viewer connected.');
    }
  });

  // ** forward click event to device
  viewParent.addEventListener(Events.OBJECTCLICKED, ({detail}: CustomEvent<ObjectClickedEvent>) => {
    const name = detail.new.object.name;
    view.notifyPointerEvent(name, PointerActionType.CLICKED, PointerType.PRIMARY);
  });

  // ** load an offline image first
  const loader = new FileLoader();
  loader.setResponseType('arraybuffer');
  maxX = 1;
  loader.load('./resources/img.msgpack', (buf) => {
    // ** show loaded image */
    {
    const msgPackDecoder = new MsgPackDecoder();
    const jo = msgPackDecoder.decode(buf as any) as CrownDataDesc<CrownImageDesc>;
    maxX = jo.data.Data.Width;
    relY = jo.data.Data.Height / maxX;
    const capData: CapDataDesc<CrownViewPresentAddDesc<CrownImageDesc>> = {
      payload: {
        Type: CrownImageCap.className,
        Iconics: [{class: 'IMAGE', data: jo.data}],
        Decorations: [],
      },
      name: 'offline_image',
      tag: 'IMAGE',
      funcName: 'call',
      materials: [ {type: 'basic', params: { }/*avoid conflicts between overlapping shapes*/ }],
    };
    director.handleCapData(capData);
    }

    {
    // ** show a shape */
    const capData: CapDataDesc<CrownViewPresentAddDesc<any, CrownShapeDecorationDesc>> = {
      payload: {
        Type: CrownShapeCap.className,
        Iconics: [{
            class: 'Shape.Rectangle',
            data: {cx: 130, cy: 10, height: 20, width: 30, orientationRad: 0} as ShapeRectangleDesc,
          },
        ],
        Decorations: [{
          class: 'View.ShapeDecoration',
          data: {
            FillColor: { A: 100, R: 0, G: 100, B: 100 },
            LineColor: { A: 200, R: 0, G: 200, B: 100 },
            LineWidth: 1,
            PointSize: 1,
            PointType: 'DOT',
          },
        }],
      },
      orientation: { scale: [1.0 / maxX, -1.0 / maxX, 1.0 / maxX], position: [-0.5, relY / 2.0, zPosShapes] },
      tag: 'overlay',
      name: '_iconicid_42',
    };
    director.handleCapData(capData);
    }

    {
    // ** show text */
    const capData: CapDataDesc<CrownViewPresentAddDesc<CrownTextDesc, CrownTextDecorationDesc>> = {
      payload: {
        Type: CrownTextCap.className,
        Iconics: [{
          class: 'Text',
          data: {value: 'Hello SICK'},
        }],
        Decorations: [{
          class: 'Text',
          data: {
            Bounds: {
              Height: 20, Width: 30,
            },
            BillboardActive: false,
            Color: { A: 255, R: 0, G: 100, B: 2000},
            FlipY: true,
            FontFamily: 'SANSSERIF',
            FontSize: 10,
            FontWeight: 'NORMAL',
            HorizontalAlignment: 'LEFT',
            VerticalAlignment: 'TOP',
            LineSpacing: 1,
            Position: { x: 130, y: 10, z: 0 },
            Rotation: 0,
            RotationAxis: {x: 0, y: 0, z: 0},
          },
        }],
      },
      materials: [ {type: 'basic', params: { }/*avoid conflicts between overlapping shapes*/ }],
      orientation: { scale: [1.0 / maxX, -1.0 / maxX, 1.0 / maxX], position: [-0.5, relY / 2.0, zPosText] },
      tag: 'overlay',
      name: 'text',
    };
    director.handleCapData(capData);
    }

  });

});

// * create text cap with necessary font resources *
async function createTextCap() {
  const baseUrl = './resources/Text2D/';
  const fonts = {
    SANSSERIF: await Text2DFontLoader.getFont(baseUrl + 'OpenSans-Regular.json', baseUrl),
    SERIF: await Text2DFontLoader.getFont(baseUrl + 'NotoSerifJP-Regular.json', baseUrl),
    MONOSPACE: await Text2DFontLoader.getFont(baseUrl + 'CutiveMono-Regular.json', baseUrl),
  };

  return new CrownTextCap(fonts);
}
